import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { configurationService } from "@/services/configuration.service";
import type { Configuration, PublicConfiguration } from "@/types/configuration";

/** Map currency code → display symbol */
const CURRENCY_SYMBOL_MAP: Record<string, string> = {
  USD: "$",
  EUR: "€",
  VND: "đ",
};

export const configurationQueryKeys = {
  all: ["configurations"] as const,
  list: () => [...configurationQueryKeys.all, "list"] as const,
  primary: () => [...configurationQueryKeys.all, "primary"] as const,
  publicPrimary: () =>
    [...configurationQueryKeys.all, "public-primary"] as const,
};

export const useConfigurations = (enabled = true) => {
  return useQuery<Configuration[], Error>({
    queryKey: configurationQueryKeys.list(),
    queryFn: () => configurationService.getAll(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const usePrimaryConfiguration = (enabled = true) => {
  return useQuery<Configuration | null, Error>({
    queryKey: configurationQueryKeys.primary(),
    queryFn: () => configurationService.getPrimary(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const usePublicPrimaryConfiguration = (enabled = true) => {
  return useQuery<PublicConfiguration | null, Error>({
    queryKey: configurationQueryKeys.publicPrimary(),
    queryFn: () => configurationService.getPublicPrimary(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Returns the currency symbol for the active configuration.
 * Falls back to '€' when the currency code is unknown or config is not loaded.
 */
export const useCurrencySymbol = (): string => {
  const { data: config } = usePublicPrimaryConfiguration();
  return useMemo(
    () => CURRENCY_SYMBOL_MAP[config?.currency ?? ""] ?? "€",
    [config?.currency],
  );
};
